type AuthProvider = "google" | "line" | "email" | "emailInvite";

type AuthStateV1 = {
  version: 1;
  userId?: string;
  secretKey?: string;
  accessToken?: string;
  refreshToken?: string;
  authProvider?: AuthProvider;

  // 端末識別ID（terminalIdentificationNumber=deviceId）
  terminalIdentificationNumber?: string;

  currentCharacterId?: string;
  currentVoiceId?: string;
  chatModeByCharacter?: Record<string, 0 | 1>;
  acknowledgedUnlockedModes?: Record<string, number[]>;
};

// PII（メールアドレス）は認証情報とは別のキーで管理する。
// 認証情報が漏洩しても email が同時に漏洩しないよう分離している。
type NewsPrefsV1 = {
  newsEmailAddress?: string;
};

const AUTH_KEY = "withmate.auth.v1";
const NEWS_PREFS_KEY = "wizmate.news-prefs.v1";

function isChatModeValue(value: unknown): value is 0 | 1 {
  return value === 0 || value === 1;
}

function sanitizeChatModeByCharacter(value: unknown): Record<string, 0 | 1> | undefined {
  if (!value || typeof value !== "object") return undefined;

  const entries = Object.entries(value as Record<string, unknown>)
    .map(([rawCharacterId, rawMode]) => {
      const characterId = normalize(rawCharacterId);
      return characterId && isChatModeValue(rawMode) ? ([characterId, rawMode] as const) : null;
    })
    .filter(Boolean) as Array<readonly [string, 0 | 1]>;

  if (entries.length === 0) return undefined;
  return Object.fromEntries(entries);
}

function sanitizeAcknowledgedUnlockedModes(value: unknown): Record<string, number[]> | undefined {
  if (!value || typeof value !== "object") return undefined;
  const entries = Object.entries(value as Record<string, unknown>)
    .map(([rawId, rawModes]) => {
      const id = normalize(rawId);
      if (!id) return null;
      if (!Array.isArray(rawModes)) return null;
      const modes = rawModes.filter((m) => typeof m === "number" && Number.isFinite(m));
      return modes.length > 0 ? ([id, modes] as const) : null;
    })
    .filter(Boolean) as Array<readonly [string, number[]]>;
  if (entries.length === 0) return undefined;
  return Object.fromEntries(entries);
}

function isAuthProvider(x: unknown): x is AuthProvider {
  return x === "google" || x === "line" || x === "email" || x === "emailInvite";
}

// secretKey はメモリのみ保持。localStorage には書かない。
// ページリロードで消えるため、accessToken 期限切れ時は再ログインが必要になる。
let _secretKeyMemory: string | undefined;

// 互換用（既存コードが参照している想定）
export const STORAGE_KEYS = {
  userId: "withmate.userId",
  secretKey: "withmate.secretKey",
  accessToken: "withmate.accessToken",
  terminalIdentificationNumber: "withmate.terminalIdentificationNumber",

  // ★ 追加（旧形式のキーとしても扱える）
  currentCharacterId: "withmate.currentCharacterId",
  currentVoiceId: "withmate.currentVoiceId",
} as const;

function normalize(v: string | null | undefined): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  if (s === "undefined" || s === "null") return null;
  return s;
}

function safeParseJson(raw: string | null): any | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isAuthStateV1(x: any): x is AuthStateV1 {
  if (!x || typeof x !== "object") return false;
  if (x.version !== 1) return false;

  const ok =
    (x.userId === undefined || typeof x.userId === "string") &&
    (x.secretKey === undefined || typeof x.secretKey === "string") &&
    (x.accessToken === undefined || typeof x.accessToken === "string") &&
    (x.refreshToken === undefined || typeof x.refreshToken === "string") &&
    (x.authProvider === undefined || isAuthProvider(x.authProvider)) &&
    (x.terminalIdentificationNumber === undefined || typeof x.terminalIdentificationNumber === "string") &&
    // ★ 追加
    (x.currentCharacterId === undefined || typeof x.currentCharacterId === "string") &&
    (x.currentVoiceId === undefined || typeof x.currentVoiceId === "string") &&
    (x.chatModeByCharacter === undefined || sanitizeChatModeByCharacter(x.chatModeByCharacter) !== undefined) &&
    (x.acknowledgedUnlockedModes === undefined || sanitizeAcknowledgedUnlockedModes(x.acknowledgedUnlockedModes) !== undefined);

  return ok;
}

function readAuthState(): AuthStateV1 {
  // 1) 新形式を読む
  const raw = localStorage.getItem(AUTH_KEY);
  const parsed = safeParseJson(raw);
  if (isAuthStateV1(parsed)) {
    // localStorage に secretKey が残っていれば一回限りメモリへ移行し削除する
    const storedSecretKey = normalize(parsed.secretKey);
    if (storedSecretKey && !_secretKeyMemory) {
      _secretKeyMemory = storedSecretKey;
      const next = { ...parsed, secretKey: undefined };
      localStorage.setItem(AUTH_KEY, JSON.stringify(next));
    }
    return {
      version: 1,
      userId: normalize(parsed.userId) ?? undefined,
      secretKey: undefined,
      accessToken: normalize(parsed.accessToken) ?? undefined,
      refreshToken: normalize(parsed.refreshToken) ?? undefined,
      authProvider: isAuthProvider(parsed.authProvider) ? parsed.authProvider : undefined,
      terminalIdentificationNumber: normalize(parsed.terminalIdentificationNumber) ?? undefined,
      // ★ 追加
      currentCharacterId: normalize(parsed.currentCharacterId) ?? undefined,
      currentVoiceId: normalize(parsed.currentVoiceId) ?? undefined,
      chatModeByCharacter: sanitizeChatModeByCharacter(parsed.chatModeByCharacter),
      acknowledgedUnlockedModes: sanitizeAcknowledgedUnlockedModes(parsed.acknowledgedUnlockedModes),
    };
  }

  // 2) 旧形式からマイグレーション（あれば）
  const legacyUserId = normalize(localStorage.getItem(STORAGE_KEYS.userId));
  const legacySecretKey = normalize(localStorage.getItem(STORAGE_KEYS.secretKey));
  const legacyAccessToken = normalize(localStorage.getItem(STORAGE_KEYS.accessToken));
  const legacyTerminalId = normalize(localStorage.getItem(STORAGE_KEYS.terminalIdentificationNumber));

  // device.ts が従来保存していたキー（あれば）も取り込む
  const legacyDeviceId = normalize(localStorage.getItem("app_device_id"));

  // ★ 追加：旧形式キー
  const legacyCurrentCharacterId = normalize(localStorage.getItem(STORAGE_KEYS.currentCharacterId));
  const legacyCurrentVoiceId = normalize(localStorage.getItem(STORAGE_KEYS.currentVoiceId));

  // 旧形式の secretKey もメモリへ移行（localStorage には書かない）
  if (legacySecretKey && !_secretKeyMemory) {
    _secretKeyMemory = legacySecretKey;
  }

  const migrated: AuthStateV1 = {
    version: 1,
    userId: legacyUserId ?? undefined,
    secretKey: undefined,
    accessToken: legacyAccessToken ?? undefined,
    terminalIdentificationNumber: legacyTerminalId ?? legacyDeviceId ?? undefined,
    // ★ 追加
    currentCharacterId: legacyCurrentCharacterId ?? undefined,
    currentVoiceId: legacyCurrentVoiceId ?? undefined,
    chatModeByCharacter: undefined,
    acknowledgedUnlockedModes: undefined,
  };

  // 旧キーが1つでもあれば新形式に保存して、旧キーを掃除
  if (
    legacyUserId ||
    legacySecretKey ||
    legacyAccessToken ||
    legacyTerminalId ||
    legacyDeviceId ||
    legacyCurrentCharacterId ||
    legacyCurrentVoiceId
  ) {
    localStorage.setItem(AUTH_KEY, JSON.stringify(migrated));
    localStorage.removeItem(STORAGE_KEYS.userId);
    localStorage.removeItem(STORAGE_KEYS.secretKey);
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.terminalIdentificationNumber);
    localStorage.removeItem(STORAGE_KEYS.currentCharacterId);
    localStorage.removeItem(STORAGE_KEYS.currentVoiceId);
    // app_device_id は互換のため残しても良いが、以後は storage を正とするなら消してOK
    // localStorage.removeItem("app_device_id");
  }

  return migrated;
}

function readNewsPrefs(): NewsPrefsV1 {
  // sessionStorage を優先して読む
  const raw = sessionStorage.getItem(NEWS_PREFS_KEY);
  const parsed = safeParseJson(raw);
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    const email = normalize((parsed as Record<string, unknown>).newsEmailAddress as string | undefined);
    return { newsEmailAddress: email ?? undefined };
  }

  // 旧形式（localStorage）からの一回限りの移行
  const legacy = localStorage.getItem(NEWS_PREFS_KEY);
  if (legacy) {
    sessionStorage.setItem(NEWS_PREFS_KEY, legacy);
    localStorage.removeItem(NEWS_PREFS_KEY);
    const legacyParsed = safeParseJson(legacy);
    if (legacyParsed && typeof legacyParsed === "object" && !Array.isArray(legacyParsed)) {
      const email = normalize((legacyParsed as Record<string, unknown>).newsEmailAddress as string | undefined);
      return { newsEmailAddress: email ?? undefined };
    }
  }

  return {};
}

function writeNewsPrefs(prefs: NewsPrefsV1): void {
  const email = normalize(prefs.newsEmailAddress);
  // localStorage の古いエントリは書き込みのたびに確実に消す
  localStorage.removeItem(NEWS_PREFS_KEY);
  if (email) {
    sessionStorage.setItem(NEWS_PREFS_KEY, JSON.stringify({ newsEmailAddress: email }));
  } else {
    sessionStorage.removeItem(NEWS_PREFS_KEY);
  }
}

function writeAuthState(next: AuthStateV1) {
  const sanitized: AuthStateV1 = {
    version: 1,
    userId: normalize(next.userId) ?? undefined,
    secretKey: undefined, // secretKey は localStorage に書かない（メモリのみ保持）
    accessToken: normalize(next.accessToken) ?? undefined,
    refreshToken: normalize(next.refreshToken) ?? undefined,
    authProvider: isAuthProvider(next.authProvider) ? next.authProvider : undefined,
    terminalIdentificationNumber: normalize(next.terminalIdentificationNumber) ?? undefined,
    // ★ 追加
    currentCharacterId: normalize(next.currentCharacterId) ?? undefined,
    currentVoiceId: normalize(next.currentVoiceId) ?? undefined,
    chatModeByCharacter: sanitizeChatModeByCharacter(next.chatModeByCharacter),
    acknowledgedUnlockedModes: sanitizeAcknowledgedUnlockedModes(next.acknowledgedUnlockedModes),
  };
  localStorage.setItem(AUTH_KEY, JSON.stringify(sanitized));
}

export const storage = {
  // 互換API：既存コード用
  get(key: (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS]): string | null {
    const s = readAuthState();
    if (key === STORAGE_KEYS.userId) return s.userId ?? null;
    if (key === STORAGE_KEYS.secretKey) return _secretKeyMemory ?? null;
    if (key === STORAGE_KEYS.accessToken) return s.accessToken ?? null;
    if (key === STORAGE_KEYS.terminalIdentificationNumber) return s.terminalIdentificationNumber ?? null;
    if (key === STORAGE_KEYS.currentCharacterId) return s.currentCharacterId ?? null;
    if (key === STORAGE_KEYS.currentVoiceId) return s.currentVoiceId ?? null;
    return null;
  },

  set(key: (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS], value: string) {
    const s = readAuthState();
    if (key === STORAGE_KEYS.userId) s.userId = value;
    if (key === STORAGE_KEYS.secretKey) { _secretKeyMemory = value; return; }
    if (key === STORAGE_KEYS.accessToken) s.accessToken = value;
    if (key === STORAGE_KEYS.terminalIdentificationNumber) s.terminalIdentificationNumber = value;
    if (key === STORAGE_KEYS.currentCharacterId) s.currentCharacterId = value;
    if (key === STORAGE_KEYS.currentVoiceId) s.currentVoiceId = value;
    writeAuthState(s);
  },

  remove(key: (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS]) {
    const s = readAuthState();
    if (key === STORAGE_KEYS.userId) delete s.userId;
    if (key === STORAGE_KEYS.secretKey) { _secretKeyMemory = undefined; return; }
    if (key === STORAGE_KEYS.accessToken) delete s.accessToken;
    if (key === STORAGE_KEYS.terminalIdentificationNumber) delete s.terminalIdentificationNumber;
    if (key === STORAGE_KEYS.currentCharacterId) delete s.currentCharacterId;
    if (key === STORAGE_KEYS.currentVoiceId) delete s.currentVoiceId;
    writeAuthState(s);
  },

  // 新API：起動処理・認証周りはこれを推奨
  getAuth(): {
    userId?: string;
    secretKey?: string;
    accessToken?: string;
    refreshToken?: string;
    authProvider?: AuthProvider;
    terminalIdentificationNumber?: string;
    // ★ 追加
    currentCharacterId?: string;
    currentVoiceId?: string;
  } {
    const s = readAuthState();
    return {
      userId: s.userId,
      secretKey: _secretKeyMemory,
      accessToken: s.accessToken,
      refreshToken: s.refreshToken,
      authProvider: s.authProvider,
      terminalIdentificationNumber: s.terminalIdentificationNumber,
      currentCharacterId: s.currentCharacterId,
      currentVoiceId: s.currentVoiceId,
    };
  },

  setAuth(partial: {
    userId?: string;
    secretKey?: string;
    accessToken?: string;
    refreshToken?: string;
    authProvider?: AuthProvider;
    terminalIdentificationNumber?: string;
    // ★ 追加
    currentCharacterId?: string;
    currentVoiceId?: string;
  }) {
    const s = readAuthState();
    const has = (key: keyof typeof partial) => Object.prototype.hasOwnProperty.call(partial, key);

    if (has("userId")) s.userId = partial.userId;
    if (has("secretKey")) _secretKeyMemory = partial.secretKey;
    if (has("accessToken")) s.accessToken = partial.accessToken;
    if (has("refreshToken")) s.refreshToken = partial.refreshToken;
    if (has("authProvider")) s.authProvider = partial.authProvider;
    if (has("terminalIdentificationNumber"))
      s.terminalIdentificationNumber = partial.terminalIdentificationNumber;

    if (has("currentCharacterId")) s.currentCharacterId = partial.currentCharacterId;
    if (has("currentVoiceId")) s.currentVoiceId = partial.currentVoiceId;

    writeAuthState(s);
  },

  getChatModeForCharacter(characterId?: string | null): 0 | 1 | null {
    const normalizedCharacterId = normalize(characterId);
    if (!normalizedCharacterId) return null;

    const s = readAuthState();
    return s.chatModeByCharacter?.[normalizedCharacterId] ?? null;
  },

  setChatModeForCharacter(characterId: string, mode: 0 | 1): void {
    const normalizedCharacterId = normalize(characterId);
    if (!normalizedCharacterId) return;

    const s = readAuthState();
    s.chatModeByCharacter = {
      ...(s.chatModeByCharacter ?? {}),
      [normalizedCharacterId]: mode,
    };
    writeAuthState(s);
  },

  removeChatModeForCharacter(characterId?: string | null): void {
    const normalizedCharacterId = normalize(characterId);
    if (!normalizedCharacterId) return;

    const s = readAuthState();
    if (
      !s.chatModeByCharacter ||
      !Object.prototype.hasOwnProperty.call(s.chatModeByCharacter, normalizedCharacterId)
    ) {
      return;
    }

    const nextChatModeByCharacter = { ...s.chatModeByCharacter };
    delete nextChatModeByCharacter[normalizedCharacterId];
    s.chatModeByCharacter =
      Object.keys(nextChatModeByCharacter).length > 0 ? nextChatModeByCharacter : undefined;
    writeAuthState(s);
  },

  getAcknowledgedUnlockedModes(characterId?: string | null): number[] {
    const id = normalize(characterId);
    if (!id) return [];
    return readAuthState().acknowledgedUnlockedModes?.[id] ?? [];
  },

  addAcknowledgedUnlockedMode(characterId: string, modeValue: number): void {
    const id = normalize(characterId);
    if (!id) return;
    const s = readAuthState();
    const existing = s.acknowledgedUnlockedModes?.[id] ?? [];
    if (existing.includes(modeValue)) return;
    s.acknowledgedUnlockedModes = {
      ...(s.acknowledgedUnlockedModes ?? {}),
      [id]: [...existing, modeValue],
    };
    writeAuthState(s);
  },

  clearAuth() {
    _secretKeyMemory = undefined;
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(NEWS_PREFS_KEY);
    sessionStorage.removeItem(NEWS_PREFS_KEY);
    // 念のため旧キーも消す
    localStorage.removeItem(STORAGE_KEYS.userId);
    localStorage.removeItem(STORAGE_KEYS.secretKey);
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.terminalIdentificationNumber);
    localStorage.removeItem(STORAGE_KEYS.currentCharacterId);
    localStorage.removeItem(STORAGE_KEYS.currentVoiceId);
    // 旧 Google SSO セッションキーの残留を清掃する
    localStorage.removeItem("wizmate.google-sso.v1");
  },

  getNewsEmail(): string | null {
    // 新キーを先に読む
    const prefs = readNewsPrefs();
    if (prefs.newsEmailAddress) return prefs.newsEmailAddress;

    // 旧 auth blob（withmate.auth.v1）に newsEmailAddress が残っている場合は移行する。
    // readAuthState() は型に存在しないフィールドを無視するため、raw JSON を直接参照する。
    const rawAuth = localStorage.getItem(AUTH_KEY);
    if (rawAuth) {
      const parsed = safeParseJson(rawAuth);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const legacy = normalize((parsed as Record<string, unknown>).newsEmailAddress as string | undefined);
        if (legacy) {
          writeNewsPrefs({ newsEmailAddress: legacy });
          const next = { ...(parsed as Record<string, unknown>) };
          delete next.newsEmailAddress;
          localStorage.setItem(AUTH_KEY, JSON.stringify(next));
          return legacy;
        }
      }
    }

    return null;
  },

  setNewsEmail(email: string): void {
    writeNewsPrefs({ newsEmailAddress: normalize(email) ?? undefined });
  },
};
